import { useEffect, useState } from "react";
import { useOrder } from "../../context/OrderContext";
import api from "../../lib/api";
import { trackPurchase } from "../../lib/analytics";
import { CheckCircle2, AlertTriangle, Mail, MessageSquare, Ruler, Store, Truck, Wrench } from "lucide-react";

/** Compact notification status card. Renders green/sent, yellow/queued, or red/failed
 * based on the order's notifications.{email,sms} block returned from the backend. */
function NotificationStatusCard({ kind, value, status }) {
  const Icon = kind === "email" ? Mail : MessageSquare;
  const label = kind === "email" ? "Email" : "Text";
  const looksLive = status?.sent === true || status?.id || status?.sid;
  const stubbed = status?.stubbed === true;
  const failed = status && status.sent === false && !stubbed;
  // Stubbed = keys not configured yet; treat as a soft "queued" so we don't lie to the customer.
  const palette = failed
    ? { bg: "#fef2f2", border: "#fecaca", text: "#991b1b", title: `${label} couldn't send`, sub: "Use the link below" }
    : stubbed
    ? { bg: "#fffbeb", border: "#fde68a", text: "#92400e", title: `${label} queued`, sub: value || "—" }
    : looksLive
    ? { bg: "#f0fdf4", border: "#bbf7d0", text: "var(--brand-text)", title: `${label} sent`, sub: value || "—" }
    : { bg: "#f3f4f6", border: "#e5e7eb", text: "#374151", title: `${label} on the way`, sub: value || "—" };
  return (
    <div
      data-testid={`${kind}-status`}
      data-state={failed ? "failed" : stubbed ? "stubbed" : looksLive ? "sent" : "pending"}
      className="flex-1 rounded-2xl p-3 border"
      style={{ background: palette.bg, borderColor: palette.border }}
    >
      <div className="flex items-center gap-1.5 mb-1">
        {failed ? <AlertTriangle className="w-4 h-4" style={{ color: palette.text }} />
          : <Icon className="w-4 h-4" style={{ color: palette.text }} />}
        <p className="text-xs font-semibold" style={{ color: palette.text }}>{palette.title}</p>
      </div>
      <p className="text-[11px] muted-text">{palette.sub}</p>
    </div>
  );
}

export default function StepDone() {
  const { state, reset } = useOrder();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (state.orderId) {
      api.get(`/orders/${state.orderId}`).then((r) => setOrder(r.data));
    }
  }, [state.orderId]);

  // Fire GA4 `purchase` event (and Google Ads conversion if AW- ID is set)
  // exactly once per order — guard with sessionStorage so a refresh on the
  // Done page doesn't double-count. We fire from here (post-redirect from
  // Stripe) rather than in the embedded checkout component so we know the
  // order is actually paid before reporting it as a conversion.
  useEffect(() => {
    if (!order || order.payment_status !== "paid" || !order.order_number) return;
    const flagKey = `gtag_purchase_fired:${order.order_number}`;
    if (typeof window !== "undefined" && window.sessionStorage?.getItem(flagKey)) return;
    trackPurchase({
      orderNumber: order.order_number,
      value: order.pricing?.total,
      currency: "USD",
      items: [{
        sku: order.product_slug,
        slug: order.product_slug,
        name: order.product_name,
        price: order.pricing?.items_subtotal,
        quantity: order.quantity,
        category: order.product_slug?.includes("sliding") ? "sliding-door" : "window-screen",
      }],
    });
    try { window.sessionStorage?.setItem(flagKey, "1"); } catch (_) { /* ignore */ }
  }, [order]);

  const measurementLink = state.orderId && state.measurementToken
    ? `${window.location.origin}/m/${state.orderId}?token=${state.measurementToken}`
    : null;

  const stillNeedsMeasurements =
    order && order.measurements_required && !order.measurements_completed;

  const proSelected = order?.upsells?.pro_measurement;
  const isPickup = order?.upsells?.pickup;
  const isDelivery = order?.upsells?.delivery;
  const isInstall = order?.upsells?.delivery_install;

  const [pickupInfo, setPickupInfo] = useState(null);
  useEffect(() => {
    if (isPickup) {
      api.get("/service-area").then((r) => setPickupInfo(r.data?.pickup)).catch(() => {});
    }
  }, [isPickup]);

  return (
    <div data-testid="step-done">
      <div className="text-center mb-6">
        <CheckCircle2 className="w-16 h-16 brand-text mx-auto" />
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-3 text-[var(--brand-text)]">
          Order placed!
        </h1>
        <p className="muted-text mt-2">
          We sent confirmation to your email and phone.
        </p>
      </div>

      <div className="option-card">
        <p className="text-xs uppercase tracking-wider muted-text">Order number</p>
        <p className="font-bold text-[var(--brand-text)] text-lg" data-testid="order-number">
          {state.orderNumber || "—"}
        </p>

        {order && (
          <>
            <div className="border-t border-[var(--brand-border)] my-3" />
            <div className="flex justify-between text-sm mb-1">
              <span className="muted-text">{order.product_name}</span>
              <span>×{order.quantity}</span>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span className="muted-text">Status</span>
              <span className="font-semibold capitalize">{order.status}</span>
            </div>
            {order.pricing.tax > 0 && (
              <div className="flex justify-between text-sm mb-1">
                <span className="muted-text">
                  Sales tax ({(order.pricing.tax_rate * 100).toFixed(2)}% · {order.pricing.tax_state})
                </span>
                <span data-testid="done-tax">${order.pricing.tax.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t border-[var(--brand-border)] my-2" />
            <div className="flex justify-between text-lg font-bold text-[var(--brand-text)]">
              <span>Paid</span>
              <span data-testid="done-total">${order.pricing.total.toFixed(2)}</span>
            </div>
          </>
        )}
      </div>

      {/* Notification confirmations — reactive based on backend send result */}
      <div className="flex gap-2 mt-3">
        <NotificationStatusCard
          kind="email"
          value={state.customer?.email}
          status={order?.notifications?.email}
        />
        <NotificationStatusCard
          kind="sms"
          value={state.customer?.phone}
          status={order?.notifications?.sms}
        />
      </div>

      {/* Fulfillment summary */}
      {order && (isPickup || isDelivery || isInstall) && (
        <div className="option-card mt-3" data-testid="fulfillment-card">
          {isPickup && (
            <>
              <Store className="w-5 h-5 brand-text mb-1" />
              <p className="font-semibold text-[var(--brand-text)]">Pickup at our shop</p>
              {pickupInfo?.address && (
                <p className="text-sm muted-text mt-1" data-testid="pickup-address">{pickupInfo.address}</p>
              )}
              {pickupInfo?.hours && (
                <p className="text-xs muted-text">{pickupInfo.hours}</p>
              )}
              <p className="text-xs muted-text mt-2">
                We&apos;ll text you when your order is ready for pickup.
              </p>
            </>
          )}
          {isDelivery && (
            <>
              <Truck className="w-5 h-5 brand-text mb-1" />
              <p className="font-semibold text-[var(--brand-text)]">Delivery scheduled</p>
              <p className="text-sm muted-text mt-1">We&apos;ll deliver to {order.customer.address_line1}.</p>
            </>
          )}
          {isInstall && (
            <>
              <Wrench className="w-5 h-5 brand-text mb-1" />
              <p className="font-semibold text-[var(--brand-text)]">Delivery + Install scheduled</p>
              <p className="text-sm muted-text mt-1">We&apos;ll be in touch to schedule a time.</p>
            </>
          )}
        </div>
      )}

      {/* Next-action card */}
      {proSelected && (
        <div className="option-card mt-4" data-testid="pro-measure-banner">
          <Ruler className="w-5 h-5 brand-text mb-1" />
          <p className="font-semibold text-[var(--brand-text)]">
            Our pro measurement team will contact you
          </p>
          <p className="text-sm muted-text mt-1">
            We&apos;ll reach out within 1 business day to schedule a visit. No measuring required from you.
          </p>
        </div>
      )}

      {!proSelected && stillNeedsMeasurements && measurementLink && (
        <div className="option-card mt-4" data-testid="measurement-link-card">
          <Ruler className="w-5 h-5 brand-text mb-1" />
          <p className="font-semibold text-[var(--brand-text)]">Enter measurements when ready</p>
          <p className="text-sm muted-text mt-1 mb-2">
            Use this link any time — also sent to your email and phone:
          </p>
          <a
            data-testid="measurement-link"
            href={measurementLink}
            className="block w-full text-center cta-btn"
            style={{ textDecoration: "none" }}
          >
            Enter measurements
          </a>
        </div>
      )}

      <button
        data-testid="new-order-btn"
        type="button"
        onClick={reset}
        className="w-full mt-6 py-3 rounded-full border-2 border-[var(--brand-border)] font-semibold"
      >
        Start new order
      </button>
    </div>
  );
}
